import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import MailActionType from "@/domain/enums/MailActionType";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import deepFocusStore, { setDeepFocusActive } from "@/stores/deepFocusStore";
import { setOpenedMail } from "@/stores/mailStore";
import { useStore } from "@tanstack/react-store";
import { Editor } from "@tiptap/react";
import { Dispatch, RefObject, SetStateAction, useEffect } from "react";
import { FocusTarget } from "../mailsList/MailAction";
import { TooltipProvider } from "../ui/tooltip";
import DeepFocusEntryButton from "./DeepFocusEntryButton";
import DeepFocusOverlay from "./DeepFocusOverlay";

interface DeepFocusSelectProps {
    mails: Array<Email>;
    loadNextPage: () => void;
    isLoading?: boolean;
    sideListMode?: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    editor: Editor;
    mailInputExpanded: boolean;
    setMailInputExpanded: (
        inputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    toInputRef: RefObject<HTMLInputElement | null>;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoadingSuggestion: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    suggestionEditor: Editor;
}

const DeepFocusSelect = ({
    mails,
    loadNextPage,
    isLoading,
    sideListMode,
    hasNextPage,
    isFetchingNextPage,
    editor,
    mailInputExpanded,
    setMailInputExpanded,
    toInputRef,
    attachments,
    setAttachments,
    acceptOrRefuseSuggestion,
    isLoadingSuggestion,
    isPendingChange,
    makeSuggestion,
    pendingSuggestion,
    setPendingSuggestion,
    suggestionEditor,
}: DeepFocusSelectProps) => {
    const { isMailDeepFocusEnabled } = useFeatureFlag();
    const isDeepFocusActive = useStore(
        deepFocusStore,
        (state) => state.isActive,
    );

    useEffect(() => () => setDeepFocusActive(false), []);

    const handleEnterDeepFocus = () => {
        setDeepFocusActive(true);
        setOpenedMail(mails[0], {
            actionType: MailActionType.REPLY,
            openIntent: false,
        });
    };

    const handleExitDeepFocus = () => {
        setDeepFocusActive(false);
    };

    return (
        <div className="relative z-modal-panel">
            <DeepFocusOverlay
                acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                attachments={attachments}
                editor={editor}
                hasNextPage={hasNextPage}
                isDeepFocusActive={isDeepFocusActive}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                isLoadingSuggestion={isLoadingSuggestion}
                isPendingChange={isPendingChange}
                loadNextPage={loadNextPage}
                mailInputExpanded={mailInputExpanded}
                mails={mails}
                makeSuggestion={makeSuggestion}
                onClose={handleExitDeepFocus}
                pendingSuggestion={pendingSuggestion}
                setAttachments={setAttachments}
                setMailInputExpanded={setMailInputExpanded}
                setPendingSuggestion={setPendingSuggestion}
                suggestionEditor={suggestionEditor}
                toInputRef={toInputRef}
            />

            <div className="absolute right-6 top-0 z-modal-backdrop flex h-15 items-center justify-center">
                <TooltipProvider delayDuration={0}>
                    <DeepFocusEntryButton
                        disabled={!isMailDeepFocusEnabled}
                        onClick={handleEnterDeepFocus}
                        showNotInPlanTooltip={!isMailDeepFocusEnabled}
                        sideListMode={sideListMode}
                    />
                </TooltipProvider>
            </div>
        </div>
    );
};

export default DeepFocusSelect;
